/**
 * Momsdeklaration deadlines (pure). Facts from docs/research-digest.md:
 *   - month/quarter periods: the 12th of the second month after the period,
 *     but the 17th when that month is January or August;
 *   - helår, enskild firma: 12 May the year after, together with INK1;
 *   - helår, AB/förening/BRF: filed together with the income tax return. The
 *     exact date depends on the bokslut month and is not in the digest, so
 *     1 July the year after the period end is used as a documented
 *     placeholder; the UI shows "tillsammans med inkomstdeklarationen" for
 *     these (see `deadlineKind`).
 * No weekend/holiday roll-forward is applied: the digest does not state it.
 */
import type { EntityType, IsoDate, VatPeriod } from '../types';

export interface YearMonth {
  year: number;
  month: number; // 1-12
}

export function parseIsoDate(date: IsoDate): { year: number; month: number; day: number } {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) throw new RangeError(`Not an ISO date: ${date}`);
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

export function toIsoDate(year: number, month: number, day: number): IsoDate {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function addMonths(ym: YearMonth, months: number): YearMonth {
  const index = ym.year * 12 + (ym.month - 1) + months;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

/** Number of days in a month (proleptic Gregorian). */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function declarationDeadline(periodEnd: IsoDate, period: VatPeriod, entity: EntityType): IsoDate {
  const end = parseIsoDate(periodEnd);
  switch (period) {
    case 'month':
    case 'quarter': {
      const due = addMonths({ year: end.year, month: end.month }, 2);
      const day = due.month === 1 || due.month === 8 ? 17 : 12;
      return toIsoDate(due.year, due.month, day);
    }
    case 'year':
      switch (entity) {
        case 'enskild_firma':
          return toIsoDate(end.year + 1, 5, 12);
        case 'aktiebolag':
        case 'ideell_forening':
        case 'bostadsrattsforening':
          return toIsoDate(end.year + 1, 7, 1);
        default: {
          const exhaustive: never = entity;
          throw new Error(`Unknown entity type ${String(exhaustive)}`);
        }
      }
    default: {
      const exhaustive: never = period;
      throw new Error(`Unknown VAT period ${String(exhaustive)}`);
    }
  }
}

/**
 * Whether the deadline is a fixed calendar date or "tillsammans med
 * inkomstdeklarationen" (helår for AB, förening and BRF), so the UI can pick
 * the right sentence instead of printing the placeholder date.
 */
export function deadlineKind(period: VatPeriod, entity: EntityType): 'exact' | 'with_income_tax_return' {
  return period === 'year' && entity !== 'enskild_firma' ? 'with_income_tax_return' : 'exact';
}

/** Whole days from `today` to `deadline`; negative when overdue. */
export function daysUntil(deadline: IsoDate, today: IsoDate): number {
  const a = parseIsoDate(deadline);
  const b = parseIsoDate(today);
  const ms = Date.UTC(a.year, a.month - 1, a.day) - Date.UTC(b.year, b.month - 1, b.day);
  return Math.round(ms / 86_400_000);
}

/** The day after `lockedThrough`: where corrections to a locked period are booked. */
export function firstOpenDate(lockedThrough: IsoDate): IsoDate {
  const { year, month, day } = parseIsoDate(lockedThrough);
  if (day < daysInMonth(year, month)) return toIsoDate(year, month, day + 1);
  return month === 12 ? toIsoDate(year + 1, 1, 1) : toIsoDate(year, month + 1, 1);
}

/** Swedish month abbreviations as written in running text ("1 okt", "12 maj"). */
export const SWEDISH_MONTH_ABBREVIATIONS: readonly string[] = [
  'jan',
  'feb',
  'mars',
  'apr',
  'maj',
  'juni',
  'juli',
  'aug',
  'sep',
  'okt',
  'nov',
  'dec',
];

/**
 * "1 okt" style date for Swedish sentences. The year is appended only when
 * asked for or when it differs from `relativeTo`'s year, so "Perioden är låst
 * – rättelsen bokförs 1 okt" reads as in the spec while a lock through
 * December yields "1 jan 2027".
 */
export function formatSwedishDate(date: IsoDate, opts: { relativeTo?: IsoDate; withYear?: boolean } = {}): string {
  const { year, month, day } = parseIsoDate(date);
  const base = `${day} ${SWEDISH_MONTH_ABBREVIATIONS[month - 1]}`;
  const relativeYear = opts.relativeTo ? parseIsoDate(opts.relativeTo).year : year;
  return opts.withYear || relativeYear !== year ? `${base} ${year}` : base;
}
